import React, { useCallback, useEffect, useState } from "react";
import { X, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import NoteCard from "./NoteCard";
import NoteComposer from "./NoteComposer";
import { useNoteDrafts } from "./useNoteDrafts";

/**
 * MeetingNotesPanel — pannello laterale per note di riunione.
 *
 * Uso: in-call (sopra l'iframe Daily, lato destro desktop / fullscreen mobile)
 *      post-call (dettaglio riunione).
 *
 * Props:
 *   meetingId
 *   meeting: oggetto (per client_id → ShareNoteModal)
 *   open: boolean
 *   onClose()
 *   variant: "overlay" (in-call) | "inline" (dettaglio)
 *   mobile: boolean (forza fullscreen anche in overlay)
 */
export default function MeetingNotesPanel({
    meetingId, meeting, open, onClose,
    variant = "overlay", mobile = false,
}) {
    const { user: me } = useAuth();
    const { draft, setDraft, clearDraft } = useNoteDrafts(meetingId, me?.id);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        if (!meetingId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/internal/meetings/${meetingId}/notes`);
            setItems(data.items || []);
        } catch (err) {
            if (err?.response?.status === 404) {
                setItems([]);
            } else {
                toast.error("Errore caricamento note");
            }
        } finally {
            setLoading(false);
        }
    }, [meetingId]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const doCreate = useCallback(async (body, shared) => {
        try {
            if (editing) {
                await api.patch(`/internal/meetings/${meetingId}/notes/${editing.id}`, {
                    body, shared_with: {
                        staff_user_ids: shared.staff_user_ids || [],
                        client_user_ids: shared.client_user_ids || [],
                    },
                    share_mode: shared.share_mode,
                });
                toast.success("Nota aggiornata");
                setEditing(null);
            } else {
                await api.post(`/internal/meetings/${meetingId}/notes`, {
                    body, shared_with: {
                        staff_user_ids: shared.staff_user_ids || [],
                        client_user_ids: shared.client_user_ids || [],
                    },
                    share_mode: shared.share_mode,
                });
                toast.success("Nota salvata");
            }
            clearDraft();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore salvataggio nota");
            throw err;
        }
    }, [meetingId, editing, load, clearDraft]);

    const doDelete = useCallback(async (note) => {
        try {
            await api.delete(`/internal/meetings/${meetingId}/notes/${note.id}`);
            toast.success("Nota eliminata");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore eliminazione");
        }
    }, [meetingId, load]);

    if (!open) return null;

    const isOverlay = variant === "overlay";
    const isFullscreen = mobile || variant === "fullscreen";
    const style = isOverlay ? (isFullscreen ? overlayFullscreenStyle : overlayDesktopStyle) : inlineStyle;

    return (
        <div data-testid="meeting-notes-panel" style={style}>
            {isOverlay && (
                <div style={headerStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <StickyNote size={14} color="#EFEFEC" />
                        <span style={titleTextStyle}>Note riunione</span>
                        <span style={counterStyle} data-testid="notes-counter">
                            ({items.length})
                        </span>
                    </div>
                    <button
                        type="button"
                        className="lt-icon-btn"
                        onClick={onClose}
                        aria-label="Chiudi pannello"
                        data-testid="meeting-notes-close-btn"
                        style={{ color: "#EFEFEC" }}
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10, background: "#F4F5F2" }}>
                {loading ? (
                    <div style={{ padding: 24, color: "#8A8F88", textAlign: "center" }}>Caricamento…</div>
                ) : items.length === 0 ? (
                    <div style={{ padding: 24, color: "#8A8F88", textAlign: "center", fontSize: 13 }}>
                        Nessuna nota ancora. Scrivi la prima qui sotto.
                    </div>
                ) : items.map((n) => (
                    <NoteCard
                        key={n.id}
                        note={n}
                        currentUserName={me?.name}
                        onEdit={(note) => {
                            setEditing(note);
                            setDraft({ body: note.body });
                        }}
                        onDelete={doDelete}
                    />
                ))}
            </div>
            <div style={{ padding: 12, borderTop: "1px solid #DADEDA", background: "#FFFFFF" }}>
                <NoteComposer
                    meeting={meeting}
                    draft={draft}
                    setDraft={setDraft}
                    initialShared={editing ? {
                        share_mode: editing.meta?.share_mode || "private",
                        staff_user_ids: editing.shared_with?.staff_user_ids || [],
                        client_user_ids: editing.shared_with?.client_user_ids || [],
                    } : undefined}
                    editing={editing}
                    onSubmit={doCreate}
                    onCancelEdit={() => { setEditing(null); setDraft({ body: "" }); }}
                />
            </div>
        </div>
    );
}

const overlayDesktopStyle = {
    position: "absolute", top: 56, right: 0, bottom: 0,
    width: 380, zIndex: 999_999,
    background: "#FFFFFF",
    borderLeft: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "-4px 0 12px rgba(0,0,0,0.15)",
    display: "flex", flexDirection: "column",
    animation: "mnpSlideIn 200ms ease-out",
};
const overlayFullscreenStyle = {
    position: "fixed", inset: 0,
    zIndex: 1_500_000,
    background: "#FFFFFF",
    display: "flex", flexDirection: "column",
};
const inlineStyle = {
    width: "100%",
    border: "1px solid #DADEDA", borderRadius: 4,
    background: "#FFFFFF",
    display: "flex", flexDirection: "column",
    minHeight: 500, maxHeight: "72vh",
};
const headerStyle = {
    height: 44, flexShrink: 0,
    background: "#181B20", color: "#EFEFEC",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 14px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
};
const titleTextStyle = {
    fontFamily: '"IBM Plex Mono", monospace', fontSize: 12,
    letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
};
const counterStyle = {
    fontSize: 11, color: "rgba(255,255,255,0.55)",
    fontFamily: '"IBM Plex Mono", monospace',
};
